import React, { useEffect, useMemo, useState } from "react";
import { Localizer } from "@/main/react/Types";
import { ClientFactory } from "@/main/common/ClientFactory";
import { getSafeToastMessageFromError, getServerErrorCode } from "@/main/common/ApiUtils";
import { MetaHandler } from "@/main/common/MetaHandler";
import { Bookmark, Languages, ListPlus, Lock, Search, Send, SquarePen } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SafeImage from "@/main/util/SafeImage";
import { Spinner } from "@/components/ui/spinner";
import {
    emitDiscussionAddedToGroup,
    emitDiscussionRemovedFromGroup,
    emitGroupAddedToDiscussion,
    emitGroupRemovedFromDiscussion,
} from "@/main/common/EventRegistry";
import { toast } from "sonner";
import {
    AjaxDiscussionRestControllerApi,
    AjaxGroupRestControllerApi,
    CreateDiscussionAddToGroupRequest,
    DiscussionsToAddToGroupDTO,
} from "@/generated-client";
import { samePrimaryLanguage } from "@/main/util/PublishLanguageMapping";
import { languageDisplayName } from "@/main/util/languageDisplayName";
import DateFormatter from "@/main/common/DateFormatter";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import {WarningBanner} from "@/main/react/component/WarningBanner";
import { DiscussionAddToGroupRequestForm } from "@/main/react/Dialog/DiscussionAddToGroupRequestForm";
import logger from "@/lib/logger";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { showCreateSeriesDialog } from "@/main/react/Dialog/UpdateSeriesSettingsDialog";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { getHideSensitiveMediaPreference, getSensitivePreferenceMode } from "@/main/common/SensitivePreference";
import {SMALL_THUMBNAIL_IMAGE_SIZE} from "@/main/util/ImageUrlHelper";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { getDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import {type WithDiscussionAuthorAvatar} from "@/main/common/DiscussionAuthorAvatar";

type Props = {
    groupPublicId: string;
    localizer: Localizer;
    onClose?: () => void;
};

type TabKey = "authored" | "participated" | "series";

const TAB_KEYS: TabKey[] = ["authored", "participated", "series"];

/**
 * The bootstrap returns only the most recent candidates per tab, so the search box has to reach the
 * server for anything older. Debounced so a typed word costs one round of requests, not one per key.
 */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The server's typed rejection for "this discussion already has a submission awaiting review here".
 *
 * <p>Branch on this, never on the message: the message is localized before it is thrown, so the
 * substring check this replaced could only ever have fired for an English reader. See the fuller note
 * on `submitAddToGroupRequest` below, which fixed the same defect on this file's other handler.
 */
const PENDING_SUBMISSION_EXISTS_ERROR_CODE = "PENDING_SUBMISSION_EXISTS";
type DiscussionWithExclusionFlag = DiscussionsToAddToGroupDTO & { isDiscussionExcluded?: boolean | null };

export function DiscussionAddToGroupCardDisplayer(props: {
    discussion: DiscussionsToAddToGroupDTO;
    localizer: Localizer;
    isAddedToGroup?: boolean;
    hasPendingRequest?: boolean;
    hasPendingLeaderReview?: boolean;
    isToggling?: boolean;
    requestMode?: boolean;
    disabled?: boolean;
    /**
     * The viewer is not a leader of this group and the post is already in it, so removal is not
     * available to them. Surfaced as `data-removal-locked` because a locked added card and a normal
     * added card look nearly identical — a spec must assert the mechanism, not the appearance
     * (rule 20). The explanation itself lives once in the dialog's banner, not per card.
     */
    removalLocked?: boolean;
    languageMismatchLabel?: string;
    onToggleAddedToGroup?: () => Promise<void> | void;
}) {
    const {
        discussion,
        localizer,
        isAddedToGroup,
        hasPendingRequest,
        hasPendingLeaderReview,
        onToggleAddedToGroup,
        isToggling,
        requestMode,
        disabled,
        removalLocked,
        languageMismatchLabel,
    } = props;
    const languageMismatch = Boolean(languageMismatchLabel);
    const discussionWithAuthorAvatar = discussion as WithDiscussionAuthorAvatar<DiscussionsToAddToGroupDTO>;
    const discussionWithFlags = discussion as DiscussionsToAddToGroupDTO & { sensitive?: boolean | null };
    const discussionWithExclusionFlag = discussion as DiscussionWithExclusionFlag;
    const df = useMemo(() => new DateFormatter(localizer), [localizer]);
    const isSensitive = Boolean(discussionWithFlags.sensitive);
    const excluded = Boolean(discussionWithExclusionFlag.isDiscussionExcluded);
    const sensitivePreference = getSensitivePreferenceMode();
    const hideSensitiveMedia = getHideSensitiveMediaPreference();

    const formattedCreated = discussion.createdAt ? df.timeSince(discussion.createdAt) : "";
    const added = Boolean(isAddedToGroup ?? discussion.isDiscussionAdded);
    const pending = Boolean(hasPendingRequest ?? discussion.isPendingAddToGroupRequest);
    const pendingLeaderReview = Boolean(hasPendingLeaderReview);
    const canRequest = Boolean(requestMode) && !added && !pending && !excluded;
    const borderClass = languageMismatch
        ? "border border-dashed border-[var(--line-color)]/60"
        : added
        ? "border border-primary"
        : pending
            ? "border border-[var(--warning-border)]"
            : pendingLeaderReview
                ? "border border-primary/40"
            : excluded
                ? "border border-border/70"
            : canRequest
                ? "border border-primary/40"
                : "border border-border";

    const handleClick = async (e?: React.SyntheticEvent) => {
        try {
            e?.stopPropagation();
            e?.preventDefault();
        } catch {
            /* ignore */
        }

        if (!onToggleAddedToGroup || isToggling || disabled) return;
        await onToggleAddedToGroup();
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            await handleClick(e);
        }
    };

    const warningCopy = getMaskedContentWarningCopy(localizer, { isSensitive });

    return (
        <div
            className={`relative w-full min-w-0 max-w-full rounded-xl bg-card p-3 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ${borderClass} ${disabled ? "cursor-not-allowed opacity-85" : "cursor-pointer"}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={onKeyDown}
            aria-pressed={added || pending || pendingLeaderReview}
            aria-disabled={disabled}
            data-playwright-id="group-add-discussion-card"
            data-discussion-public-id={discussion.publicId ?? ""}
            data-removal-locked={removalLocked ? "true" : undefined}
        >
            <div className="flex min-w-0 gap-2 items-start">
                {discussion.imageUrl && discussion.imageUrl.length > 0 ? (
                    <div className="hidden h-24 w-24 shrink-0 overflow-hidden rounded-md bg-[var(--background2)] sm:block">
                        <SensitiveMediaGuard
                            isSensitive={isSensitive}
                            hideSensitiveMedia={hideSensitiveMedia}
                            sensitivePreference={sensitivePreference}
                            variant="card"
                            overlayStacking="below-link-overlay"
                            badgeLabel={warningCopy.badgeLabel}
                            label={warningCopy.label}
                            helperText={warningCopy.helperText}
                            revealText={warningCopy.revealText}
                            className="h-full w-full overflow-hidden rounded-md"
                        >
                            <SafeImage
                                src={discussion.imageUrl}
                                alt={discussion.title ?? ""}
                                seavienImageSize={SMALL_THUMBNAIL_IMAGE_SIZE}
                                wrapperClassName="w-full h-full"
                                className="object-cover w-full h-full rounded-md"
                                onError={() => {}}
                            />
                        </SensitiveMediaGuard>
                    </div>
                ) : null}

                <div className="flex-1 min-w-0">
                    {/* The corner slot is IN FLOW and floated, not `absolute`.
                        It holds either a 20px bookmark glyph or a status chip that is a full localized
                        sentence — "Waiting for group leaders" is ~159px in English and longer in Spanish.
                        While it was `absolute top-2 right-2` nothing reserved its width, and the fixed
                        `pe-24` gutter that stood in for that reservation was a GUESS about a localized
                        string: it reserved 96px against a chip that needs up to ~159px, so the chip
                        painted over the title in every locale, and it spent those 96px of a 229px title
                        on the majority of cards that show only the glyph. Floated, the title's first
                        line wraps around whatever width the chip actually took and later lines recover
                        the full column — nothing is estimated. `relative z-20` is kept: the slot still
                        has to sit above the sensitive-media overlay in the thumbnail column.
                        Pinned across all four pickers by `dialogCardTitleGutter.spec.tsx`. */}
                    <div className="relative z-20 float-right ms-2" data-playwright-id="picker-card-corner-slot">
                        {languageMismatch ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                <Languages className="size-3" aria-hidden="true" />
                                {languageMismatchLabel}
                            </span>
                        ) : isToggling ? (
                            <Spinner className="w-5 h-5 text-neutral" />
                        ) : excluded ? (
                            <span className="rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                {localizer("group.discussion_submission.status.excluded")}
                            </span>
                        ) : pending ? (
                            <span className="rounded-full bg-[var(--warning-icon-bg)] px-2 py-1 text-[11px] font-medium text-[var(--warning)]">
                                {localizer("discussion.request.modal.request_pending")}
                            </span>
                        ) : pendingLeaderReview ? (
                            <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                {localizer("group.discussion_submission.status.waiting_for_leaders")}
                            </span>
                        ) : canRequest ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                <Send className="size-3" aria-hidden="true" />
                                {localizer("discussion.request.modal.submit")}
                            </span>
                        ) : added ? (
                            <Bookmark className="w-5 h-5 text-primary fill-current stroke-0" aria-hidden="true" />
                        ) : (
                            <Bookmark className="w-5 h-5 fill-background1 text-muted" aria-hidden="true" />
                        )}
                    </div>
                    <h4 className="wrap-anywhere font-semibold text-base">
                        <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{discussion.title}</span>
                            {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </span>
                    </h4>

                    <AuthorAttribution localizer={localizer}
                        name={getDisplayUsername(discussion.authorUsername, localizer)}
                        imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
                        avatarSeed={discussion.authorUsername}
                        details={formattedCreated ? <time dateTime={discussion.createdAt}>{formattedCreated}</time> : null}
                        className="mt-1 text-xs"
                        avatarClassName="size-5"
                    />

                    <p className="mt-2 text-sm line-clamp-2 wrap-anywhere text-muted-foreground">
                        {discussion.excerpt}
                    </p>

                    <div className="text-xs text-muted-foreground mt-2">
                        {discussion.privateAccess ? localizer("group.private") : localizer("group.public")}
                    </div>

                    {canRequest ? (
                        <div className="mt-2 flex items-center gap-1 text-xs text-primary">
                            <Lock className="size-3" aria-hidden="true" />
                            {localizer("discussion.request.modal.click_to_request")}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export function AddDiscussionsToGroupDialogInner({ groupPublicId, localizer, onClose }: Props) {
    const [loading, setLoading] = useState(true);
    const [authoredDiscussions, setAuthoredDiscussions] = useState<DiscussionsToAddToGroupDTO[] | null>(null);
    const [participatedDiscussions, setParticipatedDiscussions] = useState<DiscussionsToAddToGroupDTO[] | null>(null);
    const [seriesDiscussions, setSeriesDiscussions] = useState<DiscussionsToAddToGroupDTO[] | null>(null);
    // The group's own language. A post is only accepted when its language matches (primary subtag);
    // posts in other languages are shown muted + non-selectable, mirroring the backend language gate.
    const [groupMainLocale, setGroupMainLocale] = useState<string | undefined>(undefined);
    // Adding and removing are not symmetric: any member may add (it raises a submission for leader
    // review), only a leader may remove. Defaults to false so the remove affordance is withheld until
    // the server says otherwise.
    const [viewerIsLeader, setViewerIsLeader] = useState(false);
    const languageGateActive = Boolean(groupMainLocale && groupMainLocale.trim());
    const [tab, setTab] = useState<TabKey>("authored");
    const [query, setQuery] = useState("");
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    // Per-tab "there are older ones behind this page", straight from the bootstrap.
    const [truncated, setTruncated] = useState<Record<TabKey, boolean>>({
        authored: false,
        participated: false,
        series: false,
    });
    // Null while no search is active OR while one is in flight — both render as "not the bootstrap
    // list", which is what keeps a stale page from flashing under a new query.
    const [searchResults, setSearchResults] = useState<Record<TabKey, DiscussionsToAddToGroupDTO[]> | null>(null);
    const [pendingRequestDiscussionIds, setPendingRequestDiscussionIds] = useState<Set<string>>(new Set());
    const [pendingLeaderReviewDiscussionIds, setPendingLeaderReviewDiscussionIds] = useState<Set<string>>(new Set());
    const [requestTargetDiscussion, setRequestTargetDiscussion] = useState<DiscussionsToAddToGroupDTO | null>(null);
    const [requestNote, setRequestNote] = useState("");
    const [submittingRequest, setSubmittingRequest] = useState(false);

    const groupClient: AjaxGroupRestControllerApi = useMemo(
        () => ClientFactory.getAjaxGroupRestControllerApi(),
        []
    );
    const discussionClient: AjaxDiscussionRestControllerApi = useMemo(
        () => ClientFactory.getAjaxDiscussionRestControllerApi(),
        []
    );

    const loggedInUsername = MetaHandler.getLoggedInUsername();

    const loadAvailableDiscussions = React.useCallback(async () => {
        const { data } = await ClientFactory.getGroupDiscussionsModalBootstrap(groupPublicId);
        return {
            authored: data?.authoredDiscussions ?? [],
            participated: data?.participatedDiscussions ?? [],
            series: data?.seriesDiscussions ?? [],
            mainLocale: data?.mainLocale ?? undefined,
            truncated: {
                authored: Boolean(data?.authoredTruncated),
                participated: Boolean(data?.participatedTruncated),
                series: Boolean(data?.seriesTruncated),
            },
            // Absent from an older backend reads as false, which withholds the remove action rather
            // than offering one that 400s. The safe direction — see the type in ClientFactory.
            viewerIsLeader: Boolean(data?.viewerIsLeader),
        };
    }, [groupPublicId]);

    const applyLoadedDiscussions = React.useCallback((authored: DiscussionsToAddToGroupDTO[], participated: DiscussionsToAddToGroupDTO[], series: DiscussionsToAddToGroupDTO[]) => {
        const pendingDiscussionIds = new Set<string>();
        [authored, participated, series].flat().forEach((discussion) => {
            if (discussion.publicId && discussion.isPendingAddToGroupRequest) {
                pendingDiscussionIds.add(discussion.publicId);
            }
        });

        setAuthoredDiscussions(authored);
        setParticipatedDiscussions(participated);
        setSeriesDiscussions(series);
        setPendingRequestDiscussionIds(pendingDiscussionIds);

        setTab((previous) => {
            if (previous === "authored" && authored.length > 0) return previous;
            if (previous === "participated" && participated.length > 0) return previous;
            if (previous === "series" && series.length > 0) return previous;
            if (authored.length > 0) return "authored";
            if (participated.length > 0) return "participated";
            if (series.length > 0) return "series";
            return previous;
        });
    }, []);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setTab("authored");
        setQuery("");
        setPendingRequestDiscussionIds(new Set());
        setPendingLeaderReviewDiscussionIds(new Set());
        setRequestTargetDiscussion(null);
        setRequestNote("");
        setSubmittingRequest(false);

        loadAvailableDiscussions()
            .then(({ authored, participated, series, mainLocale, truncated: truncatedFlags, viewerIsLeader: leader }) => {
                if (!mounted) return;

                setGroupMainLocale(mainLocale);
                setTruncated(truncatedFlags);
                setViewerIsLeader(leader);
                applyLoadedDiscussions(authored, participated, series);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                logger.error("Failed loading discussions JSON for group modal", err);
                toast.error(localizer("groups.modal.toast_error"));
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [applyLoadedDiscussions, loadAvailableDiscussions, localizer]);

    const startToggling = (id: string) => setTogglingIds((prev) => new Set(prev).add(id));
    const stopToggling = (id: string) =>
        setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

    /**
     * Applies a row patch to every list a card can currently be rendered from.
     *
     * The search results are a fourth list, not a view over the other three: a card the member reached
     * by searching past the recency cap exists ONLY there, so patching the bootstrap lists alone leaves
     * its added/pending state stale until the next keystroke.
     */
    const applyToAllCandidateLists = React.useCallback(
        (update: (prev: DiscussionsToAddToGroupDTO[] | null) => DiscussionsToAddToGroupDTO[] | null) => {
            setAuthoredDiscussions(update);
            setParticipatedDiscussions(update);
            setSeriesDiscussions(update);
            setSearchResults((prev) => prev && {
                authored: update(prev.authored) ?? prev.authored,
                participated: update(prev.participated) ?? prev.participated,
                series: update(prev.series) ?? prev.series,
            });
        },
        [],
    );

    const updateDiscussionLists = (discussionPublicId: string, nextAddedValue: boolean) => {
        const update = (prev: DiscussionsToAddToGroupDTO[] | null) => {
            if (!prev) return prev;
            return prev.map((item) =>
                item.publicId === discussionPublicId
                    ? { ...item, isDiscussionAdded: nextAddedValue, isPendingAddToGroupRequest: nextAddedValue ? false : item.isPendingAddToGroupRequest }
                    : item
            );
        };

        applyToAllCandidateLists(update);
    };

    const markPendingRequest = (discussionPublicId: string) => {
        setPendingRequestDiscussionIds((prev) => {
            if (prev.has(discussionPublicId)) return prev;
            const next = new Set(prev);
            next.add(discussionPublicId);
            return next;
        });

        const updatePending = (prev: DiscussionsToAddToGroupDTO[] | null) => {
            if (!prev) return prev;
            return prev.map((item) =>
                item.publicId === discussionPublicId
                    ? { ...item, isPendingAddToGroupRequest: true }
                    : item
            );
        };

        applyToAllCandidateLists(updatePending);
    };

    const clearPendingRequest = (discussionPublicId: string) => {
        setPendingRequestDiscussionIds((prev) => {
            if (!prev.has(discussionPublicId)) return prev;
            const next = new Set(prev);
            next.delete(discussionPublicId);
            return next;
        });

        const updatePending = (prev: DiscussionsToAddToGroupDTO[] | null) => {
            if (!prev) return prev;
            return prev.map((item) =>
                item.publicId === discussionPublicId
                    ? { ...item, isPendingAddToGroupRequest: false }
                    : item
            );
        };

        applyToAllCandidateLists(updatePending);
    };

    const markPendingLeaderReview = (discussionPublicId: string) => {
        setPendingLeaderReviewDiscussionIds((prev) => {
            if (prev.has(discussionPublicId)) return prev;
            const next = new Set(prev);
            next.add(discussionPublicId);
            return next;
        });
    };

    const clearPendingLeaderReview = (discussionPublicId: string) => {
        setPendingLeaderReviewDiscussionIds((prev) => {
            if (!prev.has(discussionPublicId)) return prev;
            const next = new Set(prev);
            next.delete(discussionPublicId);
            return next;
        });
    };

    const requiresAuthorApproval = (discussion: DiscussionsToAddToGroupDTO) =>
        Boolean(discussion.privateAccess) && discussion.authorUsername !== loggedInUsername;

    const goToCreateDiscussion = React.useCallback(() => {
        onClose?.();
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                navigateToUrl("/create/discussion", { source: "group-dialog-create-discussion" });
            }, 0);
        }
    }, [onClose]);

    const goToExploreDiscussions = React.useCallback(() => {
        onClose?.();
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                navigateToUrl("/", { source: "group-dialog-explore-discussions" });
            }, 0);
        }
    }, [onClose]);

    const openCreateSeries = React.useCallback(() => {
        onClose?.();
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                showCreateSeriesDialog(localizer);
            }, 0);
        }
    }, [localizer, onClose]);

    async function createAddToGroupRequest(discussionPublicId: string, note?: string) {
        const trimmedNote = note?.trim();
        const payload: CreateDiscussionAddToGroupRequest = trimmedNote ? { note: trimmedNote } : {};

        await discussionClient.createAddToGroupRequest(
            discussionPublicId,
            groupPublicId,
            payload
        );
    }

    function openRequestForm(discussion: DiscussionsToAddToGroupDTO) {
        setRequestTargetDiscussion(discussion);
        setRequestNote("");
    }

    function closeRequestForm() {
        if (submittingRequest) return;
        setRequestTargetDiscussion(null);
        setRequestNote("");
    }

    async function submitAddToGroupRequest() {
        if (!requestTargetDiscussion?.publicId || submittingRequest) return;

        setSubmittingRequest(true);

        try {
            await createAddToGroupRequest(requestTargetDiscussion.publicId, requestNote);
            markPendingRequest(requestTargetDiscussion.publicId);
            setRequestTargetDiscussion(null);
            setRequestNote("");
            toast.success(localizer("discussion.request.modal.request_sent"));
        } catch (err) {
            logger.error("Failed creating add-to-group request from group modal", err);
            const message = getSafeToastMessageFromError(err, localizer("groups.modal.toast_error"));
            /*
             * Re-read the authoritative state instead of inferring it from the error TEXT.
             *
             * This used to match the English substring "pending request already exists", which was wrong
             * twice over. The server sends a DIFFERENT message for this case
             * (`group.discussion_submission.error.pending_submission_exists` — "You already have a
             * submission awaiting review for this post in this group"), so the match never fired; and the
             * message is LOCALIZED, so even the intended one could only ever match for English readers.
             * The result was a submit button that stayed enabled after a refusal, and authors clicking it
             * again and again: on 2026-08-22 one author failed 29 of 124 calls (23%) while 80+ other
             * members sat at exactly zero, and the one Portuguese reader in the same window could not
             * have recovered by any spelling of this check.
             *
             * A re-fetch is correct for the whole family rather than for the one message we happened to
             * name: whatever the server refused for — already pending, already added, no longer
             * eligible — the bootstrap carries the real flags, and a stale local Set is the actual
             * defect. `isPendingAddToGroupRequest` is server-provided, so this also repairs a set that
             * was stale on arrival because the submission was made in another tab or session.
             */
            try {
                const {authored, participated, series} = await loadAvailableDiscussions();
                applyLoadedDiscussions(authored, participated, series);
                setRequestTargetDiscussion(null);
                setRequestNote("");
            } catch (refreshErr) {
                // Never let the repair replace the original, more useful error in the toast.
                logger.error("Failed refreshing add-to-group state after a rejected submission", refreshErr);
            }
            toast.error(message);
        } finally {
            setSubmittingRequest(false);
        }
    }

    async function toggleDiscussion(discussion: DiscussionsToAddToGroupDTO) {
        if (loading || !discussion.publicId) return;

        const discussionWithExclusionFlag = discussion as DiscussionWithExclusionFlag;
        if (discussionWithExclusionFlag.isDiscussionExcluded) return;

        const currentlyAdded = Boolean(discussion.isDiscussionAdded);
        const discussionPublicId = discussion.publicId;
        const hasPendingRequest = pendingRequestDiscussionIds.has(discussion.publicId);

        if (togglingIds.has(discussionPublicId) || (!currentlyAdded && hasPendingRequest)) return;

        // Only a leader may remove. The card is already rendered non-interactive for a non-leader, so
        // reaching here means a keyboard/programmatic path got through — refuse rather than issue a
        // DELETE that can only 400. This is the guard, not the affordance: both are required, because
        // the affordance alone was what shipped and it is what let one member fire 15 refusals in 64
        // seconds on 2026-08-30.
        if (currentlyAdded && !viewerIsLeader) return;

        startToggling(discussionPublicId);

        try {
            if (currentlyAdded) {
                await groupClient.removeDiscussionFromGroup(groupPublicId, discussionPublicId);
                clearPendingRequest(discussionPublicId);
                clearPendingLeaderReview(discussionPublicId);
            } else {
                const { data } = await ClientFactory.addDiscussionToGroupWithResult(groupPublicId, discussionPublicId);
                clearPendingRequest(discussionPublicId);
                const addedAfterSave = data?.status === "ADDED" || data?.status === "ALREADY_ADDED";

                if (!addedAfterSave) {
                    markPendingLeaderReview(discussionPublicId);
                    toast.success(localizer("group.discussion_submission.request_sent"));
                    return;
                }

                clearPendingLeaderReview(discussionPublicId);
            }

            updateDiscussionLists(discussionPublicId, !currentlyAdded);

            const updatedDiscussion = {
                ...discussion,
                isDiscussionAdded: !currentlyAdded,
            };

            if (!currentlyAdded) {
                emitDiscussionAddedToGroup({ groupPublicId, discussion: updatedDiscussion });
                toast.success(localizer("groups.modal.toast_added"));
            } else {
                emitDiscussionRemovedFromGroup({ groupPublicId, discussion: updatedDiscussion });
                toast.success(localizer("groups.modal.toast_removed"));
            }

            const payloadGroup: any = {
                publicId: groupPublicId,
                name: "",
            };

            if (!currentlyAdded) {
                emitGroupAddedToDiscussion({
                    discussionPublicId,
                    group: payloadGroup,
                });
            } else {
                emitGroupRemovedFromDiscussion({
                    discussionPublicId,
                    group: payloadGroup,
                });
            }
        } catch (err) {
            logger.error("Failed toggling discussion in group", err);
            const message = getSafeToastMessageFromError(err, localizer("groups.modal.toast_error"));
            // The server's typed code, never the message text: the message is localized before it is
            // thrown, so the substring match this replaced could not fire for the Spanish and
            // Portuguese members who took 17 of these 400s in 12h on 2026-08-25. Same reasoning as
            // the re-fetch in submitAddToGroupRequest above.
            if (getServerErrorCode(err) === PENDING_SUBMISSION_EXISTS_ERROR_CODE) {
                markPendingRequest(discussionPublicId);
            }
            toast.error(message);
        } finally {
            stopToggling(discussionPublicId);
        }
    }

    async function handleDiscussionAction(discussion: DiscussionsToAddToGroupDTO) {
        if (loading || !discussion.publicId) return;

        const discussionWithExclusionFlag = discussion as DiscussionWithExclusionFlag;
        if (discussionWithExclusionFlag.isDiscussionExcluded) return;

        const currentlyAdded = Boolean(discussion.isDiscussionAdded);
        const hasPendingRequest = pendingRequestDiscussionIds.has(discussion.publicId);

        if (togglingIds.has(discussion.publicId) || (!currentlyAdded && (hasPendingRequest || submittingRequest))) return;

        if (!currentlyAdded && requiresAuthorApproval(discussion)) {
            openRequestForm(discussion);
            return;
        }

        await toggleDiscussion(discussion);
    }

    const trimmedQuery = useMemo(() => query.trim(), [query]);

    // Searching hits the per-source endpoint, never modal-bootstrap — this dialog's spec asserts the
    // bootstrap is read exactly once per open. All three sources are queried together so the
    // "jump to the tab that has matches" behaviour below keeps the counts it needs.
    useEffect(() => {
        if (!trimmedQuery) {
            setSearchResults(null);
            return;
        }

        let active = true;
        setSearchResults(null);
        const handle = window.setTimeout(() => {
            Promise.all(
                TAB_KEYS.map((source) =>
                    ClientFactory.searchGroupDiscussionCandidates(groupPublicId, source, trimmedQuery)
                        .then(({ data }) => data ?? []),
                ),
            )
                .then(([authored, participated, series]) => {
                    if (!active) return;
                    setSearchResults({ authored, participated, series });
                })
                .catch((err) => {
                    if (!active) return;
                    // eslint-disable-next-line no-console
                    logger.error("Failed searching discussions for group modal", err);
                    toast.error(localizer("groups.modal.toast_error"));
                    // Land on a definite empty result rather than an endless spinner.
                    setSearchResults({ authored: [], participated: [], series: [] });
                });
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            active = false;
            window.clearTimeout(handle);
        };
    }, [trimmedQuery, groupPublicId, localizer]);

    const bootstrapLists = useMemo<Record<TabKey, DiscussionsToAddToGroupDTO[] | null>>(() => ({
        authored: authoredDiscussions,
        participated: participatedDiscussions,
        series: seriesDiscussions,
    }), [authoredDiscussions, participatedDiscussions, seriesDiscussions]);

    // Null means "nothing to show yet" — the bootstrap or the search is still in flight — and renders
    // the spinner. An empty array is a real answer.
    const visibleFor = React.useCallback((source: TabKey): DiscussionsToAddToGroupDTO[] | null => {
        if (!trimmedQuery) return bootstrapLists[source];
        return searchResults ? searchResults[source] : null;
    }, [bootstrapLists, searchResults, trimmedQuery]);

    // Split a visible list into posts the group can accept (same language) and posts shown muted for
    // explanation (a different language). No group language → everything is "matching".
    const partitionByLanguage = React.useCallback((items: DiscussionsToAddToGroupDTO[] | null) => {
        if (!items || !languageGateActive) {
            return { matching: items ?? [], nonMatching: [] as DiscussionsToAddToGroupDTO[] };
        }
        const matching: DiscussionsToAddToGroupDTO[] = [];
        const nonMatching: DiscussionsToAddToGroupDTO[] = [];
        for (const discussion of items) {
            if (samePrimaryLanguage(groupMainLocale, (discussion as { locale?: string | null }).locale)) {
                matching.push(discussion);
            } else {
                nonMatching.push(discussion);
            }
        }
        return { matching, nonMatching };
    }, [groupMainLocale, languageGateActive]);

    // Localized display name of a post's language, for the muted "In {language}" badge. The shared
    // reader reduces `pt-BR` / `en_US` / `EN` to the primary subtag before the lookup (SV-73).
    const languageLabelFor = React.useCallback(
        (locale: string | null | undefined): string => languageDisplayName(localizer, locale),
        [localizer],
    );

    useEffect(() => {
        if (!trimmedQuery || !searchResults) return;
        if (searchResults[tab].length > 0) return;

        const nextTab = TAB_KEYS.find((candidate) => searchResults[candidate].length > 0);
        if (nextTab && nextTab !== tab) {
            setTab(nextTab);
        }
    }, [trimmedQuery, tab, searchResults]);

    function renderDiscussionEntry(discussion: DiscussionsToAddToGroupDTO) {
        const discussionWithExclusionFlag = discussion as DiscussionWithExclusionFlag;
        const pendingRequest = Boolean(discussion.publicId && pendingRequestDiscussionIds.has(discussion.publicId));
        const pendingLeaderReview = Boolean(discussion.publicId && pendingLeaderReviewDiscussionIds.has(discussion.publicId));
        // A non-leader cannot remove, so an already-added post is read-only for them. The banner above
        // the list carries the explanation once, rather than a chip on every added card.
        const removalLocked = Boolean(discussion.isDiscussionAdded) && !viewerIsLeader;
        const disabled = loading
            || Boolean(discussionWithExclusionFlag.isDiscussionExcluded)
            || removalLocked
            || (!discussion.isDiscussionAdded && (pendingRequest || pendingLeaderReview));

        return (
            <DiscussionAddToGroupCardDisplayer
                key={discussion.publicId}
                discussion={discussion}
                localizer={localizer}
                isAddedToGroup={Boolean(discussion.isDiscussionAdded)}
                hasPendingRequest={pendingRequest}
                hasPendingLeaderReview={pendingLeaderReview}
                isToggling={Boolean(discussion.publicId && togglingIds.has(discussion.publicId))}
                requestMode={requiresAuthorApproval(discussion)}
                disabled={disabled}
                removalLocked={removalLocked}
                onToggleAddedToGroup={async () => {
                    if (!discussion.publicId || disabled) return;
                    await handleDiscussionAction(discussion);
                }}
            />
        );
    }

    function renderMismatchDiscussionEntry(discussion: DiscussionsToAddToGroupDTO) {
        return (
            <DiscussionAddToGroupCardDisplayer
                key={discussion.publicId}
                discussion={discussion}
                localizer={localizer}
                disabled
                languageMismatchLabel={languageLabelFor((discussion as { locale?: string | null }).locale)}
            />
        );
    }

    function renderTabContent(source: TabKey) {
        const visibleItems = visibleFor(source);

        // `visibleItems === null` covers both the opening bootstrap and an in-flight search.
        if (loading || visibleItems === null) {
            return (
                <div className="w-full flex items-center justify-center py-6">
                    <Spinner className="w-10 h-10 text-neutral" />
                </div>
            );
        }

        if (visibleItems.length > 0) {
            const { matching, nonMatching } = partitionByLanguage(visibleItems);
            return (
                <div className="flex w-full flex-col gap-2">
                    {matching.map((discussion) => renderDiscussionEntry(discussion))}
                    {nonMatching.length > 0 ? (
                        <div className="mt-2 flex flex-col gap-2 border-t border-[var(--line-color)]/60 pt-3">
                            <div className="px-1">
                                <div className="text-xs font-medium text-muted-foreground">
                                    {localizer("groups.modal.discussion_language_mismatch.section_title")}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {localizer("groups.modal.discussion_language_mismatch.section_help")}
                                </div>
                            </div>
                            {nonMatching.map((discussion) => renderMismatchDiscussionEntry(discussion))}
                        </div>
                    ) : null}
                </div>
            );
        }

        if (trimmedQuery) {
            return (
                <PaginatedListEmptyState
                    icon={Search}
                    title={localizer("groups.modal.discussion_search_empty.title")}
                    description={localizer("groups.modal.discussion_search_empty.description")}
                />
            );
        }

        const action = source === "authored"
            ? {
                label: localizer("groups.modal.new_discussion"),
                onClick: goToCreateDiscussion,
                icon: SquarePen,
            }
            : source === "participated"
                ? {
                    label: localizer("literal.explore_discussions"),
                    onClick: goToExploreDiscussions,
                    icon: Search,
                }
                : {
                    label: localizer("series.modal.new_series"),
                    onClick: openCreateSeries,
                    icon: ListPlus,
                };

        const emptyTitle = source === "authored"
            ? localizer("groups.modal.empty.authored.title")
            : source === "participated"
                ? localizer("groups.modal.empty.participated.title")
                : localizer("groups.modal.empty.series.title");
        const emptyDescription = source === "authored"
            ? localizer("groups.modal.empty.authored.description")
            : source === "participated"
                ? localizer("groups.modal.empty.participated.description")
                : localizer("groups.modal.empty.series.description");

        return (
            <PaginatedListEmptyState
                icon={action.icon}
                title={emptyTitle}
                description={emptyDescription}
                action={action}
            />
        );
    }
    const hasDiscussionsRequiringApproval = [authoredDiscussions, participatedDiscussions, seriesDiscussions]
        .flatMap((items) => items ?? [])
        .some((discussion) => !discussion.isDiscussionAdded && requiresAuthorApproval(discussion));

    // Only shown when it is actually load-bearing: the viewer cannot remove AND there is at least one
    // post already in the group for them to have tried it on. A non-leader with nothing added sees no
    // banner, because nothing is locked for them.
    const hasLockedRemovals = !viewerIsLeader
        && [authoredDiscussions, participatedDiscussions, seriesDiscussions]
            .flatMap((items) => items ?? [])
            .some((discussion) => Boolean(discussion.isDiscussionAdded));

    const renderPrivateRequestForm = () => {
        if (!requestTargetDiscussion) return null;

        const requestTargetDiscussionWithAuthorAvatar = requestTargetDiscussion as WithDiscussionAuthorAvatar<typeof requestTargetDiscussion>;

        return (
            <DiscussionAddToGroupRequestForm
                localizer={localizer}
                backLabel={localizer("discussion.request.modal.back_to_discussions")}
                targetLabel={localizer("discussion.request.modal.selected_discussion")}
                noteInputId="group-discussion-request-note"
                note={requestNote}
                submitting={submittingRequest}
                onBack={closeRequestForm}
                onCancel={closeRequestForm}
                onSubmit={submitAddToGroupRequest}
                onNoteChange={setRequestNote}
            >
                <div className="flex gap-3 items-start min-w-0">
                    {requestTargetDiscussion.imageUrl ? (
                        <div className="hidden h-16 w-16 shrink-0 overflow-hidden rounded-md bg-[var(--background2)] sm:block">
                            <SafeImage
                                src={requestTargetDiscussion.imageUrl}
                                alt={requestTargetDiscussion.title ?? ""}
                                seavienImageSize={SMALL_THUMBNAIL_IMAGE_SIZE}
                                wrapperClassName="w-full h-full"
                                className="object-cover w-full h-full rounded-md"
                                onError={() => {}}
                            />
                        </div>
                    ) : null}

                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-base">{requestTargetDiscussion.title}</div>
                        <AuthorAttribution localizer={localizer}
                            name={getDisplayUsername(requestTargetDiscussion.authorUsername, localizer)}
                            imageUrl={requestTargetDiscussionWithAuthorAvatar.authorProfileImageUrl}
                            avatarSeed={requestTargetDiscussion.authorUsername}
                            className="mt-1 text-xs"
                            avatarClassName="size-5"
                        />
                        {/* Body copy in the confirmation preview, so `text-muted-foreground` — the same
                            reason as the card row's excerpt and meta line above: `text-neutral` is
                            `--neutral-color` (gray-400), a border/icon tint, ~2.3:1 on this surface. */}
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                            {requestTargetDiscussion.excerpt}
                        </p>
                        <div className="mt-2 text-xs text-muted-foreground">
                            {requestTargetDiscussion.privateAccess ? localizer("group.private") : localizer("group.public")}
                        </div>
                    </div>
                </div>
            </DiscussionAddToGroupRequestForm>
        );
    };

    return (
        <div className="relative w-full" data-playwright-id="group-add-discussion-dialog">
            <div className="flex flex-col gap-4 items-start justify-start w-full">
                {hasDiscussionsRequiringApproval && !requestTargetDiscussion ? (
                    <WarningBanner
                        icon={Lock}
                        description={localizer("discussion.request.modal.help")}
                    />
                ) : null}

                {hasLockedRemovals && !requestTargetDiscussion ? (
                    <div className="w-full" data-playwright-id="group-add-discussion-remove-locked-banner">
                        <WarningBanner
                            icon={Lock}
                            description={localizer("group.discussion.remove_leader_only")}
                        />
                    </div>
                ) : null}

                {requestTargetDiscussion ? renderPrivateRequestForm() : null}

                {!requestTargetDiscussion && (
                    <>
                        <div className="relative w-full">
                            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                            <input
                                type="search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={localizer("groups.modal.discussion_search_placeholder")}
                                aria-label={localizer("groups.modal.discussion_search_placeholder")}
                                className="w-full rounded-lg border border-border bg-input/70 py-3 pl-10 pr-4 text-sm [@media(pointer:coarse)]:text-base text-text-main outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                data-playwright-id="group-add-discussion-search"
                            />
                        </div>

                        {!trimmedQuery && truncated[tab] ? (
                            <p
                                className="w-full text-xs text-muted-foreground"
                                data-playwright-id="group-add-discussion-truncated-hint"
                            >
                                {localizer("shared.chooser.candidates_truncated")}
                            </p>
                        ) : null}

                    <Tabs className="w-full" value={tab} onValueChange={(value) => setTab(value as TabKey)}>
                        <TabsList variant={"line"} className="w-full justify-center">
                            <TabsTrigger value="authored">
                                {localizer("groups.modal.tab_authored")}
                            </TabsTrigger>
                            <TabsTrigger value="participated">
                                {localizer("groups.modal.tab_participated")}
                            </TabsTrigger>
                            <TabsTrigger value="series">
                                {localizer("groups.modal.tab_series")}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="authored" className={"w-full max-w-full"}>
                            {renderTabContent("authored")}
                        </TabsContent>

                        <TabsContent value="participated" className={"w-full max-w-full"}>
                            {renderTabContent("participated")}
                        </TabsContent>

                        <TabsContent value="series" className={"w-full max-w-full"}>
                            {renderTabContent("series")}
                        </TabsContent>
                    </Tabs>
                    </>
                )}
            </div>
        </div>
    );
}

export function showAddDiscussionsToGroupDialog(groupPublicId: string, localizer: Localizer) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={
                <div id="discussions-modal-title">
                    {localizer("groups.modal.discussions_title")}
                </div>
            }
            width="xl"
        >
            <AddDiscussionsToGroupDialogInner
                groupPublicId={groupPublicId}
                localizer={localizer}
                onClose={() => onOpenChange(false)}
            />
        </SimpleDialog>
    ));
}
